import { useQuery } from '@tanstack/react-query';

import { hasKey } from '~/utils';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import {
  ApplicationAdmissionConditionResource,
  ListApplicationAdmissionConditionsResponse,
  ApplicationFinalOfferConditionResource,
  ListApplicationFinalOfferConditionsResponse,
  ApplicationWaitlistConditionResource,
  ListApplicationWaitlistConditionsResponse,
} from '@ab/ams-school-api/types/private/v1';

export type ApplicationAdmissionCondition = ApplicationAdmissionConditionResource['attributes'] & { id: string };
export type ApplicationFinalOfferCondition = ApplicationFinalOfferConditionResource['attributes'] & { id: string };
export type ApplicationWaitlistCondition = ApplicationWaitlistConditionResource['attributes'] & { id: string };

export enum ConditionType {
  ADMISSION_CONDITIONS = 'admission_conditions',
  FINAL_OFFER_CONDITIONS = 'final_offer_conditions',
  WAITLIST_CONDITIONS = 'waitlist_conditions',
}

export function useOfferConditions(conditionType: ConditionType) {
  const request = useRequest();

  const { isLoading, isFetching, error, data, isError } = useQuery({
    queryKey: [`application_${conditionType}`],
    queryFn: async () => {
      const response = await request<
        | ListApplicationAdmissionConditionsResponse
        | ListApplicationFinalOfferConditionsResponse
        | ListApplicationWaitlistConditionsResponse
      >(`${config.apiHost}/private/v1/${conditionType}?page[number]=1&page[size]=100`, {
        method: 'GET',
        isExpectedResponse,
      });

      const conditions = response.data;
      return conditions.map((condition) => {
        return {
          id: condition.id,
          name: condition.attributes.name,
          code: condition.attributes.code,
        };
      });
    },
  });
  return {
    isLoadingCondition: isLoading,
    isFetchingCondition: isFetching,
    conditionError: error,
    conditions: data,
    isConditionError: isError,
  };
}

function isExpectedResponse(
  res: unknown
): res is
  | ListApplicationAdmissionConditionsResponse
  | ListApplicationFinalOfferConditionsResponse
  | ListApplicationWaitlistConditionsResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
